import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { SpeakingOrder, StarPicker } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import {
  countSelectionsForUserOnTask,
  getNextRoute,
  getPreviousRoute
} from "../../util/general.util";
import { demoUsers } from "../../util/demoData.util";

const TASK_ID = 8;

const Task8b = ({ summary = false, type, demoWorkshop = false }) => {
  const { t } = useTranslation();

  const [isStarted, setIsStarted] = useState(false);
  const [starPositions, setStarPositions] = useState({});
  const [finishedTask, setFinishedTask] = useState(false);
  const [readyUsers, setReadyUsers] = useState([]);
  const [taskOrderUsers, setTaskOrderUsers] = useState([]);

  const {
    socket,
    user: { id, name, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  useEffect(() => {
    if (demoWorkshop) setTaskOrderUsers(demoUsers || []);

    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      if (!myTaskStatus) return;
      setIsStarted(myTaskStatus?.ready);
      setReadyUsers(myTaskStatus?.readyUsers || []);
    });

    socket?.emit("taskOrder", TASK_ID, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) setIsStarted(true);
      if (myTaskStatus?.readyUsers) setReadyUsers(myTaskStatus?.readyUsers || []);
    });
  }, [id]);

  const handleNext = () => {
    if (!isStarted) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
      setIsStarted(true);
      return;
    } else {
      const nextRoute = getNextRoute("/task-8/a", platform);
      console.log("nextRoute", nextRoute);
      socket.emit("navigate", nextRoute);
    }
  };

  const handleComplete = () => {
    const selectionCount = countSelectionsForUserOnTask(starPositions, type, id);
    if (selectionCount < 2) {
      toast.info(t("task8.a.selectAtLeastTwo"));
      return;
    }

    socket?.emit("updateTask8", starPositions);
    socket.emit("taskActivityStatus", {
      taskId: TASK_ID,
      status: { ready: true, readyUsers: [...readyUsers, id] }
    });
    setFinishedTask(true);
  };

  const handleButtonClick = () => {
    if (!isStarted && role !== defaultRoles.participant) {
      return handleNext;
    } else if (isStarted && role !== defaultRoles.moderator && !finishedTask) {
      return handleComplete;
    } else if (role !== defaultRoles.participant && finishedTask) {
      return handleNext;
    } else if (role === defaultRoles.moderator) {
      return handleNext;
    } else {
      return null;
    }
  };

  const buttonText =
    (role === defaultRoles.moderator && isStarted) ||
    (role === defaultRoles.moderatorAndParticipant && finishedTask)
      ? t("buttonNext") + " →"
      : role !== defaultRoles.participant && !isStarted
        ? t("task8.a.start") + " →"
        : role !== defaultRoles.moderator && isStarted
          ? t("task8.a.complete") + " →"
          : null;

  const buttonColor =
    (role === defaultRoles.moderator && isStarted) ||
    (role === defaultRoles.moderatorAndParticipant && finishedTask)
      ? "green"
      : role !== defaultRoles.participant && !isStarted
        ? "green"
        : role !== defaultRoles.moderator && isStarted
          ? "orange"
          : "green";

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-8/a`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      buttonColor={buttonColor}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-4">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted
            ? finishedTask || role === defaultRoles.moderator
              ? t("task8.a.finished.title")
              : t("task8.a.started.title")
            : t("task8.a.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html: isStarted
              ? finishedTask || role === defaultRoles.moderator
                ? t("task8.a.finished.description")
                : t("task8.a.started.description")
              : t("task8.a.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <div className="flex flex-col md:items-start gap-4 w-full">
          <div className="w-full">
            <div className="mx-auto flex items-center justify-center mb-4">
              <SpeakingOrder participants={taskOrderUsers} filterOnlineParticipants={true} />
            </div>
            <StarPicker
              socket={socket}
              id={"task8"}
              updateId={"updateTask8"}
              type={type}
              name={name}
              userId={id}
              role={role}
              platform={platform}
              summary={summary}
              demoWorkshop={demoWorkshop}
              hideBottom={true}
              updateStarPositions={setStarPositions}
              finishedTask={finishedTask}
            />
          </div>

          <div className="mt-4 w-full px-4">
            <Conference isLinear={true} demoWorkshop={demoWorkshop} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full">
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl col-span-1 w-full h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4">
              {t("task8.a.card.title")}
            </h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task8.a.card.list.item1") }}
            />
          </div>
          <div className="col-span-1 md:col-span-2 w-full">
            <Conference smallSpace={true} demoWorkshop={demoWorkshop} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task8b;
